import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';

import {
  ContactSupportViaServiceCloudVars,
  useContactSupportViaServiceCloud,
} from '@frontend/api';
import { Button, useToast } from '@frontend/legacy-components';

import { SUPPORT_EMAIL } from 'constants/all';
import messages from 'constants/messages';

import { getUserEmail } from 'store/session/selectors';

import type { SupportFormData } from '../types';

export const useSupportFormInternals = (successCallback: () => void) => {
  const supportFormRef = useRef<HTMLFormElement>(null);

  const createToast = useToast();

  const userEmailAddress = useSelector(getUserEmail);
  const userHasEmailAddress =
    typeof userEmailAddress === 'string' && !!userEmailAddress;

  const supportFormInitialValues: SupportFormData = useMemo(
    () => ({
      type: undefined,
      priority: undefined,
      subject: '',
      description: '',
      email: userHasEmailAddress ? userEmailAddress : '',
    }),
    [userHasEmailAddress, userEmailAddress],
  );

  const supportFormValidationSchema = useMemo(
    () =>
      object().shape({
        type: string(),
        priority: string(),
        subject: string().required(messages.REQUIRED),
        description: string().required(messages.REQUIRED),
        email: userHasEmailAddress
          ? string().notRequired()
          : string().email(messages.INVALID_EMAIL).required(messages.REQUIRED),
      }),
    [userHasEmailAddress],
  );

  const onError = useCallback(() => {
    createToast({
      status: 'error',
      title: "Couldn't send message",
      description: (
        <>
          Try again once. If the problem persists, email{' '}
          <Button
            variant="link"
            as="a"
            href={`mailto:${SUPPORT_EMAIL}`}
            fontSize="inherit"
            lineHeight="inherit"
          >
            {SUPPORT_EMAIL}
          </Button>
        </>
      ),
    });
  }, [createToast]);

  const onSuccess = useCallback(() => {
    createToast({
      status: 'success',
      title: 'Message sent successfully',
      description: 'We will get back to you shortly.',
    });
    successCallback();
  }, [createToast, successCallback]);

  const useContactSupportOptions = useMemo(
    () => ({
      onSuccess,
      onError,
    }),
    [onSuccess, onError],
  );

  const { mutate: contactSupport } = useContactSupportViaServiceCloud(
    useContactSupportOptions,
  );

  const submitSupportForm = useCallback(
    (values: SupportFormData) => {
      const valuesWithOverrides: ContactSupportViaServiceCloudVars['formValues'] =
        {
          ...values,
          type: values.type ?? '',
          priority: values.priority ?? '',
          email:
            typeof userEmailAddress === 'string'
              ? userEmailAddress
              : values.email,
        };

      return new Promise<void>((resolve, reject) => {
        try {
          contactSupport({
            formValues: valuesWithOverrides,
            formElement: supportFormRef.current,
          });
          resolve();
        } catch {
          onError();
          reject();
        }
      });
    },
    [contactSupport, onError, userEmailAddress],
  );

  const returnValue = useMemo(
    () => ({
      supportFormRef,
      submitSupportForm,
      supportFormInitialValues,
      supportFormValidationSchema,
    }),
    [
      supportFormRef,
      submitSupportForm,
      supportFormInitialValues,
      supportFormValidationSchema,
    ],
  );

  return returnValue;
};
